<template>
  <div class="bhe-schedule">
    <div
      v-for="day in days"
      :key="day.key"
      class="bhe-day"
    >
      <div class="bhe-day-grid">
        <div class="bhe-day-toggles">
          <p
            class="bhe-day-label"
            :class="{ 'bhe-day-label--open': schedule[day.value].isOpen }"
          >
            {{ day.label }}
          </p>

          <div class="bhe-toggle-group">
            <vs-switch
              v-model="schedule[day.value].isOpen"
              color="#6a1f4c"
              @input="onToggleOpen(day.value, $event)"
            />
            <span class="bhe-toggle-label">
              {{ schedule[day.value].isOpen
                  ? $t('dash.presenceManagement.hours.open')
                  : $t('dash.presenceManagement.hours.closed') }}
            </span>
          </div>

          <div class="bhe-toggle-group">
            <vs-switch
              v-model="schedule[day.value].isAllDay"
              color="#6a1f4c"
              :disabled="!schedule[day.value].isOpen"
            />
            <span class="bhe-toggle-label bhe-toggle-label--secondary">
              {{ $t('dash.presenceManagement.hours.allDay') }}
            </span>
          </div>
        </div>

        <div class="bhe-slots">
          <template v-if="schedule[day.value].isOpen && !schedule[day.value].isAllDay">
            <div
              v-for="(slot, idx) in schedule[day.value].slots"
              :key="idx"
              class="bhe-slot-row"
            >
              <input
                type="time"
                v-model="slot.from"
                class="bhe-time-input"
                :class="{ 'bhe-time-input--error': hasFromError(day.value, idx) }"
              />
              <span class="bhe-time-sep">{{ $t('dash.presenceManagement.hours.to') }}</span>
              <input
                type="time"
                v-model="slot.to"
                class="bhe-time-input"
                :class="{ 'bhe-time-input--error': hasToError(day.value, idx) }"
              />
              <span class="bhe-slot-actions">
                <button
                  v-if="idx > 0"
                  class="bhe-icon-btn bhe-icon-btn--del"
                  @click="removeSlot(day.value, idx)"
                >
                  <i class="material-icons">delete_outline</i>
                </button>
                <button
                  v-if="idx === 0 && schedule[day.value].slots.length < 4"
                  class="bhe-icon-btn bhe-icon-btn--add"
                  @click="addSlot(day.value)"
                >
                  <i class="material-icons">add_circle_outline</i>
                </button>
              </span>
            </div>
          </template>
        </div>

        <button class="bhe-apply-btn" @click="applyToAll(day.value)">
          <i class="material-icons">auto_fix_high</i>
          <span>{{ $t('dash.presenceManagement.hours.applyToAll') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const DAYS = [
  { key: 'monday',    value: 'MONDAY' },
  { key: 'tuesday',   value: 'TUESDAY' },
  { key: 'wednesday', value: 'WEDNESDAY' },
  { key: 'thursday',  value: 'THURSDAY' },
  { key: 'friday',    value: 'FRIDAY' },
  { key: 'saturday',  value: 'SATURDAY' },
  { key: 'sunday',    value: 'SUNDAY' }
]

const DAY_FALLBACK = {
  monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
  thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche'
}

function buildDefaultDayConfig() {
  return { isOpen: false, isAllDay: false, slots: [{ from: '09:00', to: '18:00' }] }
}

/**
 * Converts a GBP regularHours.periods array to the UI schedule object.
 * Multiple periods with the same openDay become multiple slots.
 * @param {Array} periods
 * @returns {Object}
 */
function gbpPeriodsToSchedule(periods) {
  const schedule = {}
  DAYS.forEach(d => { schedule[d.value] = buildDefaultDayConfig() })

  if (!Array.isArray(periods)) return schedule

  periods.forEach(period => {
    const day = period.openDay
    if (!schedule[day]) return

    const isAllDay = period.openTime === '00:00' && (period.closeTime === '00:00' || period.closeTime === '24:00')

    if (!schedule[day].isOpen) {
      schedule[day].isOpen = true
      schedule[day].isAllDay = isAllDay
      if (!isAllDay) {
        schedule[day].slots = [{ from: period.openTime || '09:00', to: period.closeTime || '18:00' }]
      }
    } else if (!isAllDay) {
      schedule[day].slots.push({ from: period.openTime || '09:00', to: period.closeTime || '18:00' })
    }
  })

  return schedule
}

function scheduleToGbpPeriods(schedule) {
  const periods = []
  Object.entries(schedule).forEach(([dayKey, day]) => {
    if (!day.isOpen) return
    if (day.isAllDay) {
      periods.push({ openDay: dayKey, openTime: '00:00', closeDay: dayKey, closeTime: '00:00' })
      return
    }
    day.slots.forEach(slot => {
      periods.push({ openDay: dayKey, openTime: slot.from, closeDay: dayKey, closeTime: slot.to })
    })
  })
  return periods
}

function timeToMinutes(timeStr) {
  if (!timeStr) return 0
  const [hours, minutes] = timeStr.split(':').map(Number)
  return hours * 60 + minutes
}

export default {
  name: 'tab-hours',

  props: {
    periods: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    days() {
      return DAYS.map(day => {
        const i18nKey = `dash.presenceManagement.days.full.${day.key}`
        const translated = this.$t(i18nKey)
        return { ...day, label: translated === i18nKey ? DAY_FALLBACK[day.key] : translated }
      })
    }
  },

  data() {
    return {
      schedule: gbpPeriodsToSchedule(this.periods)
    }
  },

  methods: {
    /** Returns the current schedule as GBP regularHours.periods. Called by the parent on save. */
    getScheduleData() {
      return { periods: scheduleToGbpPeriods(this.schedule) }
    },

    onToggleOpen(dayKey, isOpen) {
      if (!isOpen) {
        this.schedule[dayKey].isAllDay = false
      }
    },

    addSlot(dayKey) {
      const slots = this.schedule[dayKey].slots
      if (slots.length >= 4) return

      const lastSlot = slots[slots.length - 1]
      const pad = n => String(n).padStart(2, '0')
      let fromH = 14, fromM = 0

      if (lastSlot?.to) {
        const [h, m] = lastSlot.to.split(':').map(Number)
        fromH = Math.min(h + 1, 23)
        fromM = m
      }
      const toH = Math.min(fromH + 2, 23)
      slots.push({ from: `${pad(fromH)}:${pad(fromM)}`, to: `${pad(toH)}:${pad(fromM)}` })
    },

    removeSlot(dayKey, idx) {
      this.schedule[dayKey].slots.splice(idx, 1)
    },

    applyToAll(sourceDayKey) {
      const source = this.schedule[sourceDayKey]
      DAYS.forEach(({ value }) => {
        if (value === sourceDayKey) return
        this.$set(this.schedule, value, {
          isOpen: source.isOpen,
          isAllDay: source.isAllDay,
          slots: source.slots.map(s => ({ from: s.from, to: s.to }))
        })
      })
    },

    hasFromError(dayValue, idx) {
      const config = this.schedule[dayValue]
      if (!config?.isOpen || config.isAllDay) return false
      const slots = config.slots
      const slot = slots[idx]
      if (!slot?.from || !slot?.to) return false
      const start = timeToMinutes(slot.from)
      const end = timeToMinutes(slot.to)
      if (start >= end) return true
      if (idx > 0 && slots[idx - 1]) {
        return timeToMinutes(slots[idx - 1].to) > start
      }
      return false
    },

    hasToError(dayValue, idx) {
      const config = this.schedule[dayValue]
      if (!config?.isOpen || config.isAllDay) return false
      const slots = config.slots
      const slot = slots[idx]
      if (!slot?.from || !slot?.to) return false
      const start = timeToMinutes(slot.from)
      const end = timeToMinutes(slot.to)
      if (start >= end) return true
      if (idx < slots.length - 1 && slots[idx + 1]) {
        return end > timeToMinutes(slots[idx + 1].from)
      }
      return false
    },

    /**
     * Validates all day slots. Shows a danger notification and returns false if any slot has errors.
     * Called by the parent (LocationEditor) before the save confirmation modal.
     * @returns {boolean}
     */
    validate() {
      for (const day of this.days) {
        const config = this.schedule[day.value]
        if (config.isOpen && !config.isAllDay) {
          for (let i = 0; i < config.slots.length; i++) {
            if (this.hasFromError(day.value, i) || this.hasToError(day.value, i)) {
              this.$vs.notify({
                title: this.$t('dash.presenceManagement.hours.invalid.title'),
                text: this.$t('dash.presenceManagement.hours.invalid.txt'),
                color: 'danger',
                position: 'top-right'
              })
              return false
            }
          }
        }
      }
      return true
    }
  }
}
</script>

<style scoped>
.bhe-schedule {
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  /* box-shadow: 0 2px 12px rgba(46, 25, 55, 0.07); */
}

.bhe-day {
  background: #fff;
  min-height: 120px;
  border-bottom: 1px solid #f0e8f6;
}

.bhe-day:last-child {
  border-bottom: 0;
}

.bhe-day-grid {
  display: grid;
  grid-template-columns: 200px 1fr auto;
  align-items: center;
  padding: 24px 28px;
  gap: 0 20px;
}

.bhe-day-toggles {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bhe-day-label {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 400;
  color: #c0afd0;
}

.bhe-day-label--open {
  font-weight: 700;
  color: #6a1f4c;
}

.bhe-toggle-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bhe-toggle-label {
  font-size: 13px;
  font-weight: 500;
  color: #3d2a4a;
  white-space: nowrap;
}

.bhe-toggle-label--secondary {
  color: #9e8dae;
}

::v-deep .bhe-toggle-group .vs-switch {
  background-color: #d8cce2 !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
  min-width: 44px !important;
  width: 44px !important;
  height: 22px !important;
  border-radius: 11px !important;
  padding: 2px !important;
  flex-shrink: 0;
  position: relative !important;
}

::v-deep .bhe-toggle-group .vs-switch.vs-switch-active {
  background-color: #6a1f4c !important;
}

::v-deep .bhe-toggle-group .vs-switch[disabled] {
  background-color: #ece5f3 !important;
  opacity: 0.7 !important;
  cursor: not-allowed !important;
}

.bhe-slots {
  display: grid;
  grid-template-columns: repeat(2, auto);
  gap: 8px 16px;
  justify-content: start;
  align-items: center;
  margin-top: 8px;
}

.bhe-slot-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 20px;
}

.bhe-time-input {
  border: 1px solid #e2d5ef;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: #3d2a4a;
  background: #fff;
  width: 100px;
  outline: none;
  transition: border-color 0.2s;
  box-shadow: none;
}

.bhe-time-input--error { border-color: #ff5c5c !important; color: #ff5c5c !important; }
.bhe-time-input:focus { border-color: #6a1f4c; }
.bhe-time-input::-webkit-calendar-picker-indicator { display: none; }

.bhe-time-sep { font-size: 13px; color: #9e8dae; }

.bhe-slot-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 26px;
  flex-shrink: 0;
}

button.bhe-icon-btn {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
  padding: 4px !important;
  border-radius: 4px !important;
  min-width: 0 !important;
  width: auto !important;
  display: inline-flex !important;
  align-items: center !important;
  transition: color 0.18s;
}

button.bhe-icon-btn i { font-size: 14px; }
button.bhe-icon-btn--del { color: #d4c1e3 !important; }
button.bhe-icon-btn--del:hover { color: #6a1f4c !important; }
button.bhe-icon-btn--add { color: #d4c1e3 !important; }
button.bhe-icon-btn--add:hover { color: #6a1f4c !important; }

button.bhe-apply-btn {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
  color: #c9b4d8 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  padding: 6px 0 !important;
  min-width: 0 !important;
  width: auto !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
  white-space: nowrap !important;
  cursor: pointer !important;
  justify-self: end;
}

button.bhe-apply-btn:hover {
  color: #6a1f4c !important;
  background: transparent !important;
  box-shadow: none !important;
}

@media (max-width: 1499px) {
  .bhe-slots { display: flex; flex-direction: column; gap: 8px; }
}

@media (max-width: 1149px) {
  .bhe-day-grid { grid-template-columns: 1fr; gap: 12px 0; padding: 16px 18px; }
  .bhe-slots { display: flex; flex-direction: column; align-items: flex-start; }
  button.bhe-apply-btn { justify-self: start !important; }
}
</style>
